/**
 * ThemeDialogHost — mounts the app Theme picker once at the root so it can be
 * opened from anywhere (any page's account menu) via the global
 * `pidly:open-theme` event. Keeping a single host means the dialog survives the
 * menu that triggered it closing, and there's exactly one instance app-wide.
 *
 * Surviving the MENU is the point; surviving the PAGE is not. Because the
 * host is mounted once and never unmounts, nothing used to take the dialog
 * down when the user navigated — so it followed them onto every workspace
 * and every other page until they found the ×. It closes on a route change
 * now, which is why it lives inside the Router.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import ThemeTunerDialog from './MainView/Dialogs/ThemeTunerDialog';
import { getStoredTheme, setStoredTheme } from '../utils/theme';

export default function ThemeDialogHost() {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme);
  const { pathname } = useLocation();

  // Navigating away closes it. Adjusted DURING RENDER rather than in an
  // effect — React's own pattern for resetting state when a prop
  // changes, and the one the react-hooks lint asks for (a setState in an
  // effect body causes a second render pass).
  //
  // Compared on pathname alone, so a query-string or hash change — which
  // doesn't take you to a different page — leaves the dialog alone.
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    if (open) setOpen(false);
  }

  useEffect(() => {
    const onOpen = () => { setTheme(getStoredTheme()); setOpen(true); };
    window.addEventListener('pidly:open-theme', onOpen);
    return () => window.removeEventListener('pidly:open-theme', onOpen);
  }, []);

  if (!open) return null;
  return createPortal(
    <ThemeTunerDialog
      theme={theme}
      onThemeChange={(id) => {
        setTheme(setStoredTheme(id));
        // Let listeners (e.g. the account page's Theme section) update
        // their "current theme" label live.
        try { window.dispatchEvent(new Event('pidly:theme-changed')); } catch { /* no-op */ }
      }}
      onClose={() => setOpen(false)}
    />,
    document.body,
  );
}
